import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs/BehaviorSubject";

import { HttpWrapperService } from "./http.wrapper.service";
import { SessionManagerService } from "./SessionManager/session-manager.service";

import { observableToPromise } from "../admin/events-screen/utils/observableToPromise";
import { SaveActionResult } from "../admin/events-screen/event-page-models/save-action-result";
import { HttpError } from "../admin/events-screen/event-page-models/http-error";
import { EventCircleIndex, EventTypeData } from "../_models/event-type-data";

@Injectable()
export class EventsTypesManagementService {
  public readonly allEventTypeData$: BehaviorSubject<EventTypeData[]> =
    new BehaviorSubject<EventTypeData[]>([]);

  constructor(
    private http: HttpWrapperService,
    private sessionManager: SessionManagerService
  ) {}

  /**
   * Serialize to the server model.
   * > note that if the circle index is null, it isn't sent to the server
   * > and the server deletes that circle index
   * @param {EventTypeData} eventType
   */
  public static serializeEventTypeForServer(eventType: EventTypeData) {
    return {
      name: eventType.name,
      isVisible: eventType.isVisible,
      isMultiday: eventType.isMultiday,
      defaultIndex: eventType.defaultIndex,
      indexes: eventType.indexes
        .map((indx) => ({
          circleId: +indx.circleId,
          index: indx.value,
        }))
        .filter((circleIndex) => circleIndex.index !== null),
    };
  }

  public static circlesAndEventTypesToEventTypeModel(
    circles: Array<{
      circleId: number;
      name: string;
      code: string;
      parent: any;
    }>,
    eventTypes: EventTypeData[]
  ) {
    const sortedCircles = EventsTypesManagementService.sortByCircleId(
      circles
    ).map((c) => ({
      circleId: c.circleId,
      circleName: c.name,
    }));

    eventTypes.forEach((entIndex) => {
      const indexes: EventCircleIndex[] = sortedCircles.map((c) => ({
        id: "",
        eventId: "",
        circleId: c.circleId.toString(),
        circleName: c.circleName,
        value: null,
      }));
      indexes.forEach((circle: EventCircleIndex) => {
        // @ts-ignore
        const relatedRecord: EventCircleIndex & { index: number } =
          entIndex.indexes.find(
            (index_) =>
              index_.circleId.toString() === circle.circleId.toString()
          );
        if (relatedRecord) {
          circle.value = relatedRecord.index;
          circle.id = relatedRecord.circleId;
        }
      });
      entIndex.indexes = indexes;
    });
    return eventTypes;
  }
  public static sortByCircleId<T extends { circleId }>(obj: T[]): T[] {
    return [...obj].sort((a, b) => a.circleId - b.circleId);
  }

  public async getAllEventTypes() {
    try {
      const allData = await Promise.all([
        observableToPromise(this.http.get("/api/global")),
        observableToPromise(this.http.get("apiV2/eventTypes")),
      ]);
      const dataGlobal = await allData[0].json();
      const dataEventTypes = await allData[1].json();

      const allCircles = dataGlobal.circles.filter((c) => c.circleId);
      return EventsTypesManagementService.circlesAndEventTypesToEventTypeModel(
        allCircles,
        dataEventTypes
      );
    } catch (e) {
      throw new Error(
        `EventsManagementService getAllEventTypes failed ${JSON.stringify(e)}`
      );
    }
  }
  public async saveEventType(
    eventType: EventTypeData
  ): Promise<SaveActionResult> {
    try {
      const serializedEventData =
        EventsTypesManagementService.serializeEventTypeForServer(eventType);

      if (+eventType.id < 1) {
        // new eventType
        const saveNewResponse = await observableToPromise(
          this.http.post("apiV2/eventTypes", serializedEventData)
        );

        if (saveNewResponse.status === 201) {
          return {
            data: saveNewResponse.json(),
            success: true,
          };
        }
        throw new HttpError(``, {
          status: saveNewResponse.status,
          additionalData: saveNewResponse.json() || saveNewResponse.toString(),
        });
      } else {
        // update exiting event
        const updateResponse = await observableToPromise(
          this.http.patch(
            `apiV2/eventTypes/${eventType.id}`,
            serializedEventData
          )
        );

        if (updateResponse.status === 200) {
          return {
            data: updateResponse,
            success: true,
          };
        }
        throw new HttpError(``, {
          status: updateResponse.status,
          additionalData: updateResponse.json() || updateResponse.toString(),
        });
      }
    } catch (e) {
      return {
        error: e,
        success: false,
      };
    }
  }

  public async mockSaveEventType(eventType: EventTypeData) {
    const allEvents = this.allEventTypeData$
      .getValue()
      .map((ev) => ({ ...ev }));
    if (+eventType.id < 0) {
      // tslint:disable-next-line:no-bitwise
      eventType.id = ((Math.random() * 10000) | 0).toString();
      allEvents.unshift(eventType);
    } else {
      const eventToUpdate = allEvents.find((ev) => ev.id === eventType.id);
      Object.keys(eventToUpdate).forEach((prop) => {
        eventToUpdate[prop] = eventType[prop];
      });
    }

    this.allEventTypeData$.next(allEvents);
  }
}



// WEBPACK FOOTER //
// ./src/app/_services/events-management.service.ts